// SensorMonitorItem.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SensorLiveGauge from './SensorLiveGauge';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { userState } from '../../../atoms';
import { apiGet } from '../../../services/_common';
import { arraySortByAscdOrder } from '../../../utils/arraySortByAscdOrder';
import { convertCO2PpmToPercent, convertCO2PpmToMgPerCubicMeter } from '../../../utils/convertCO2Unit';
import IssueGuide from '../../IssueGuide';
import { ERROR, O2_RANGE, SENSOR_LIMIT_LIST, SENSOR_UNIT_LIST } from '../../../_constants';
import SkeletonTable from '../../SkeletonTable';

const Root = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  padding: 1rem 1rem 0.875rem;

  .sensor-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: 6px;

    .sensor-name {
      display: flex;
      align-items: center;
      gap: 6px;

      .device-name {
        display: block;
        border-radius: 20px;
        color: var(--primary);
        border: 1px solid var(--primary);
        padding: 4px 6px;
        font-size: 12px;
        line-height: 1.2;
      }
    }

    .ripple {
      position: relative;
      width: 2rem;
      height: 2rem;
    }
    .ripple::before,
    .ripple::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: 50%;
      border: 3px solid var(--primary);
      opacity: 0.7;
      // animation: ripple calc(1.8s * 24px) ease-out infinite;
      animation: ripple 1.8s ease-out infinite;
    }
    .ripple::after {
      animation-delay: 0.45s;
      opacity: 0.4;
    }

    @keyframes ripple {
      0% {
        transform: scale(0.2);
        opacity: 0.85;
      }
      100% {
        transform: scale(1);
        opacity: 0;
      }
    }
  }

  .sensor-main {
    display: flex;
    flex-direction: column;
    width: 100%;

    .sensor-value {
      display: flex;
      align-items: flex-end;
      gap: 10px;

      .value {
        font-size: 76px;
        font-weight: 700;
        line-height: 1;
      }

      .unit {
        font-size: 24px;
        color: var(--text-tertiary);
        padding-bottom: 0.5rem;
      }
    }
  }

  .device-btn {
    display: none;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;
    width: auto;
    padding: 10px;
    height: auto;
    padding: 8px 2px;

    span {
      color: var(--text-tertiary);
    }

    &:hover {
      background: var(--op-bg-15) !important;

      span {
        color: var(--text-primary);
      }
    }
  }

  &:hover {
    .device-btn {
      display: flex;
    }
  }

  .prev-btn {
    left: 0;
  }

  .next-btn {
    right: 0;
  }
`;

interface Props {
  sensorCd: string;
  sensorName: string;
  dCd: string;
  autoRefresh: boolean;
  refetchInterval?: number;
}

// 상태 컬러
const getStatusColor = (status: string) => {
  switch (status) {
    case '좋음':
      return 'var(--primary)';
    case '주의':
      return 'var(--filled-green)';
    case '경고':
      return 'var(--filled-amber)';
    case '위험':
      return 'var(--filled-red)';
    default:
      return 'var(--text-tertiary)';
  }
};

const SensorMonitorItem = ({ sensorCd, sensorName, dCd: uniqueKey, autoRefresh, refetchInterval = 6000 }: Props) => {
  const { t } = useTranslation();
  const { hCd, sCd } = useRecoilValue(userState);
  // const [isPlaying, setIsPlaying] = useState(false);
  const [currentDeviceIdx, setCurrentDeviceIdx] = useState<number>(0); // 현재 장비 인덱스

  // type 값을 센서 코드로 변환하는 함수
  const getSensorCodeByType = (type: string): string => {
    const typeMap: { [key: string]: string } = {
      '01': 'O2',
      '02': 'NO2',
      '03': 'CO',
      '04': 'CO2',
      '05': 'H2S',
      '06': 'CH4',
      '07': 'CH2O',
      '08': 'O3',
      '09': 'PM2.5',
      '10': 'PM10',
      '11': 'TEMP',
      '12': 'HUMI',
      '13': 'VOC',
      '14': 'PM1',
      '15': 'FEELS',
    };
    return typeMap[type] || '';
  };
  const sensorType = getSensorCodeByType(sensorCd);
  const defaultUnit = SENSOR_UNIT_LIST[sensorType] || 'ppm';

  // 센서 범위 설정 데이터 조회 API
  const fetchAllSensorRanges = async () => {
    try {
      const res = await apiGet({ path: '/sensor/range', req: { hCd, sCd } });
      const rangeMap: { [key: string]: { level1: number; level2: number; level3: number; unit?: string; min: number; max: number } } = {};

      res.data.data.rangeList.forEach((list: any) => {
        const { srCd, unit, level1, level2, level3 } = list;
        const limits = SENSOR_LIMIT_LIST[srCd] || { min: 0, max: 100 };
        const initialUnit = SENSOR_UNIT_LIST[srCd] || 'ppm';

        const getConvertedLimit = (val: any) => {
          if (!val) return 0;
          const numVal = Number(val);

          if (srCd === 'CO2') {
            if (unit === '%') {
              return convertCO2PpmToPercent(numVal);
            }
            if (unit === 'mg/m³') {
              return convertCO2PpmToMgPerCubicMeter(numVal);
            }
          }

          return numVal;
        };

        rangeMap[srCd] = {
          level1: level1 !== '' ? level1 : null,
          level2: level2 !== '' ? level2 : null,
          level3: level3 !== '' ? level3 : null,
          unit: unit ?? initialUnit,
          min: getConvertedLimit(limits.min) ?? 0,
          max: getConvertedLimit(limits.max) ?? 0,
        };
      });

      // 체감온도 범위는 하드코딩 적용
      rangeMap.FEELS = {
        level1: 31,
        level2: 33,
        level3: 35,
        min: 0,
        max: 100,
        unit: '°C',
      };

      return rangeMap;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  // 모든 센서의 range 정보를 가져오는 쿼리
  const {
    data: allSensorRanges,
    isLoading: isLoadingAllSensorRanges,
    // isFetching: isFetchingAllSensorRanges,
    isError: isErrorAllSensorRanges,
  } = useQuery([`allSensorRangesGet_monitoring`, hCd, sCd], () => fetchAllSensorRanges(), { enabled: !!hCd && !!sCd, cacheTime: 0 });

  const getMergedData = (arr: any[]) => {
    // const result: WgasMergeType[] = [];
    const result: any[] = [];
    arr?.forEach((obj) => {
      const existingItem = result.find((el) => el.sub_cd === obj.sub_cd);
      if (!existingItem) result.push(obj);
    });
    return result;
  };

  // 무선 유해가스측정 장비 목록 조회
  const fetchSensorData = async () => {
    try {
      const req = { hCd, sCd };
      const res = await apiGet({ path: '/sensor/wgas/widget', req });
      const result = getMergedData(res.data.data);

      return result;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  // 무선 유해가스측정 장비 목록 조회
  const fetchUlist = async () => {
    try {
      const req = { hCd, sCd, subCd: '09' };
      const res = await apiGet({ path: '/sensor/ulist', req });
      const useYList = res.data.data.sensorUList.filter((el: any) => el.useYn === 'Y' && el.uListYn === 'Y');
      const result = arraySortByAscdOrder(useYList, 'etc1');
      return result;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const {
    data: sensorData,
    isLoading: isLoadingSensorData,
    // isFetching: isFetchingSensorData,
    isRefetching: isRefetchingSensorData,
    isError: isErrorSensorData,
    refetch: refetchSensor,
  } = useQuery([`tareaSensorGet_monitoring`, hCd, sCd], () => fetchSensorData(), {
    enabled: !!hCd && !!sCd && sCd !== '00000',
    // ...(refetchInterval && { refetchInterval }),
  });

  const {
    data: ulistData,
    isLoading: isLoadingUlistData,
    // isFetching: isFetchingUlistData,
    isRefetching: isRefetchingUlistData,
    isError: isErrorUlistData,
    refetch: refetchUlist,
  } = useQuery([`sensorUListGet_monitoring`, hCd, sCd], () => fetchUlist(), {
    enabled: !!hCd && !!sCd && sCd !== '00000',
    // ...(refetchInterval && { refetchInterval: 6000 * 60 }),
  });

  /* 상황판 자동 갱신
  useEffect(() => {
    const autoRefreshWidget = () => {
      refetchSensor();
      refetchUlist();
    };

    if (autoRefresh) {
      const interval = setInterval(autoRefreshWidget, 60 * 1000);
      return () => clearInterval(interval);
    }

    return () => {};
  }, [autoRefresh]);
  */

  // const onClickTooglePlay = () => setIsPlaying((prev) => !prev);

  const onClickGetDeviceData = useCallback(
    (type: 'prev' | 'next') => {
      if (!sensorData || !sensorData.length) return;

      setCurrentDeviceIdx((prevIdx) => {
        const nextIdx = prevIdx + 1 > sensorData.length - 1 ? 0 : prevIdx + 1;
        const prevIdxVal = prevIdx - 1 < 0 ? sensorData.length - 1 : prevIdx - 1;

        return type === 'prev' ? prevIdxVal : nextIdx;
      });
    },
    [sensorData]
  );

  /* 센서 장비 자동 전환 */
  /*
  useEffect(() => {
    if (sensorData && sensorData.length > 1) {
      const interval = setInterval(() => {
        onClickGetDeviceData('next');
      }, 10000);

      return () => clearInterval(interval);
    }

    return () => {};
  }, [sensorData]);
  */

  // 새로고침 버튼 클릭 시
  const onClickRefresh = () => {
    refetchSensor();
    refetchUlist();
  };

  const renderSensorDataValue = (sensorDataList: any, ulistData2: any): number | null => {
    const getSensorCd = sensorType;
    const co2Unit = allSensorRanges?.CO2?.unit ?? 'ppm';
    const sensorFindIndex = ulistData2.findIndex((el: any) => el.ssCd === getSensorCd);
    const setSensorKey = `data${sensorFindIndex + 1}`;

    // CO2 센서일 경우 단위 변경 및 단위에 따른 범위 계산 필요 - data4 -> 이산화탄소
    if (sensorType === 'CO2') {
      if (co2Unit === '%') {
        return sensorDataList[currentDeviceIdx].data4 ? convertCO2PpmToPercent(Number(sensorDataList[currentDeviceIdx].data4)) : null;
      }
      if (co2Unit === 'mg/m³') {
        return sensorDataList[currentDeviceIdx].data4 ? convertCO2PpmToMgPerCubicMeter(Number(sensorDataList[currentDeviceIdx].data4)) : null;
      }
    }

    return sensorDataList.length > 0 && sensorDataList[currentDeviceIdx][setSensorKey] ? Number(sensorDataList[currentDeviceIdx][setSensorKey]) : null;
  };

  const getSensorValueColor = () => {
    const value = (sensorData && renderSensorDataValue(sensorData, ulistData)) ?? null;
    const sData = allSensorRanges?.[sensorType];

    if (!value) return 'var(--text-quaternary)';

    // 온도(TEMP) / 습도(HUMI) 특수 처리
    if (sensorType === 'TEMP') {
      if (value < 18) return 'var(--primary)'; // 저온 (파랑)
      if (value >= 27) return 'var(--filled-red)'; // 고온 (빨강)
      return 'var(--filled-green)'; // 적정 (초록)
    }

    if (sensorType === 'HUMI') {
      if (value <= 29) return 'var(--primary)'; // 건조 (파랑)
      if (value >= 60) return 'var(--filled-red)'; // 다습 (빨강)
      return 'var(--filled-green)'; // 적정 (초록)
    }

    // 체감온도(FEELS) 특수 처리
    if (sensorType === 'FEELS') {
      if (value < 31) return 'var(--primary)'; // 적정 (파랑)
      if (value < 33) return 'var(--filled-green)'; // 관심 (초록)
      if (value < 35) return 'var(--filled-amber)'; // 주의 (노랑)
      if (value < 38) return 'var(--filled-orange)'; // 경고 (주황)
      return 'var(--filled-red)'; // 위험 (빨강)
    }

    if (sensorType === 'O2') {
      const matchedRange = [...O2_RANGE].reverse().find((o2Range) => {
        const [lowerMin, lowerMax] = o2Range.lower;
        const [upperMin, upperMax] = o2Range.upper;

        // 하한선(lower) 체크: -1은 무시, 값이 범위 내에 있는지 확인
        const isLowerBreached = (lowerMin !== -1 && value < lowerMin) || (lowerMax !== -1 && value <= lowerMax);

        // 상한선(upper) 체크: -1은 무시
        const isUpperBreached = (upperMin !== -1 && value > upperMin) || (upperMax !== -1 && value >= upperMax);

        return isLowerBreached || isUpperBreached;
      });

      const statusIdx = matchedRange ? Number(matchedRange.level) : 0;

      if (statusIdx === 3) return 'var(--filled-red)';
      if (statusIdx === 2) return 'var(--filled-amber)';
      if (statusIdx === 1) return 'var(--filled-green)';
      return 'var(--primary)';
    }

    if (sensorType === 'VOC') {
      if (value === 0) return 'var(--primary)';
      if (value === 1) return 'var(--filled-green)';
      if (value === 2) return 'var(--filled-amber)';
      return 'var(--filled-red)';
    }

    // 일반 센서 및 산소(O2) 처리 (서버 설정값 sData 기준)
    if (sData) {
      const { level1, level2, level3 } = sData;

      if (!level1 || !level2 || !level3) return 'var(--text-quaternary)'; // sData가 있지만 각 범위에 값이 비어있는 경우( 범위 미지정인 경우 ) 기본컬러

      if (value < level1) return getStatusColor('좋음');
      if (value < level2) return getStatusColor('주의');
      if (value < level3) return getStatusColor('경고');
      return getStatusColor('위험');
    }

    return 'var(--text-quaternary)'; // 기본값
  };

  const loading = isLoadingAllSensorRanges || isLoadingUlistData || isLoadingSensorData || isLoadingAllSensorRanges;

  if (loading) {
    return (
      <div style={{ width: '100%', height: '100%' }}>
        <SkeletonTable rows={0} mainRowHeight={30} mainRowWidth='40%' />
        <SkeletonTable rows={0} mainRowWidth='100%' mainRowHeight={50} rowHeight={50} />
      </div>
    );
  }

  if (isErrorAllSensorRanges || isErrorSensorData || isErrorUlistData) return <IssueGuide type='error' text={ERROR} size={6} />;

  return (
    <Root className='sensor-monitor-item'>
      <div className='sensor-header'>
        <div className='sensor-name'>
          <span className='name'>{sensorName}</span>
          {sensorData && sensorData.length > 0 && <span className='device-name'>{sensorData[currentDeviceIdx]?.s_name}</span>}
        </div>
        {/* <div className='refresh-btn' role='presentation' onClick={() => setIsPlaying((prev) => !prev)}>
          <span className='material-symbols-rounded'>{isPlaying ? 'pause' : 'play_arrow'}</span>
        </div> */}
        {!autoRefresh &&
          (isRefetchingSensorData || isRefetchingUlistData ? (
            <div className='card'>
              <div className='loader ripple' aria-hidden='true' />
            </div>
          ) : (
            <div className='refresh-btn' role='presentation' onClick={onClickRefresh}>
              <span className='material-symbols-rounded'>refresh</span>
            </div>
          ))}
      </div>

      <div className='sensor-main'>
        <div className='sensor-value'>
          <span className='value' style={{ color: getSensorValueColor() }}>
            {(sensorData && renderSensorDataValue(sensorData, ulistData)) ?? '-'}
          </span>
          <span className='unit'>{allSensorRanges && Object.keys(allSensorRanges).length !== 0 ? allSensorRanges[sensorType]?.unit ?? defaultUnit : defaultUnit}</span>
        </div>
        {/* 실제 게이지 바 */}
        <SensorLiveGauge sensorType={sensorType} value={renderSensorDataValue(sensorData, ulistData)} ranges={allSensorRanges} />
      </div>

      {/* 센서 장비 선택 */}
      {sensorData && sensorData.length > 1 && (
        <>
          <button type='button' className='device-btn prev-btn' onClick={() => onClickGetDeviceData('prev')}>
            <span className='material-symbols-rounded'>chevron_left</span>
          </button>
          <button type='button' className='device-btn next-btn' onClick={() => onClickGetDeviceData('next')}>
            <span className='material-symbols-rounded'>chevron_right</span>
          </button>
        </>
      )}
    </Root>
  );
};

export default SensorMonitorItem;
